/**
 * DeliveryHistory Hook
 * 배달 이력 데이터 관리 및 API 호출 로직
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getDeliveryHistory, getPreAcctYn } from '../api/deliveryApi';
import { 
    DeliveryHistoryRequest, 
    DeliveryHistoryStats, 
    DEFAULT_DELIVERY_HISTORY_STATS 
} from '../types/deliveryHistory';

/**
 * 배달 이력 Hook
 */
export const useDeliveryHistory = () => {
    // 상태 관리
    const [deliveryStats, setDeliveryStats] = useState<DeliveryHistoryStats>(DEFAULT_DELIVERY_HISTORY_STATS);
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [preAcctYn, setPreAcctYn] = useState<string>('N');
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    
    // 어떤 필드가 마지막으로 변경됐는지 추적하여 짝을 맞출 때 사용
    const lastChangedRef = useRef<'from' | 'to' | null>(null);

    // 문자열(YYYY-MM-DD 또는 YYYYMMDD) -> 로컬 Date (타임존 이슈 방지)
    const parseDateYmd = useCallback((value: string) => {
        if (!value) return new Date(NaN);
        let y = 0, m = 0, d = 0;
        if (value.includes('-')) {
            const parts = value.split('-');
            if (parts.length !== 3) return new Date(NaN);
            y = Number(parts[0]);
            m = Number(parts[1]);
            d = Number(parts[2]);
        } else if (value.length === 8) {
            y = Number(value.slice(0, 4));
            m = Number(value.slice(4, 6));
            d = Number(value.slice(6, 8));
        } else {
            return new Date(NaN);
        }
        return new Date(y, m - 1, d);
    }, []);

    // fromDate 선택용: preAcctYn에 따라 다른 날짜 옵션 생성
    const dateOptions = (() => {
        const options: string[] = [];

        if (preAcctYn === 'Y') {
            // 선정산: 주정산과 동일한 fromDate 옵션 (수요일 기준 주간 단위)
            const now = new Date();
            // 이전 달의 첫째 날
            const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            // 현재 달의 마지막 날
            const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

            // prevMonthStart부터 currentMonthEnd까지 순회하며 수요일만 수집
            const cursor = new Date(prevMonthStart);

            // 첫 수요일로 이동
            const day = cursor.getDay(); // 0=일, 3=수
            const toNextWednesday = (3 - day + 7) % 7; // 수요일까지 남은 일수
            cursor.setDate(cursor.getDate() + toNextWednesday);

            while (cursor <= currentMonthEnd) {
                // 현재 날짜보다 이전인 수요일만 포함 (미래 수요일 제외)
                if (cursor < now) {
                    const y = cursor.getFullYear();
                    const m = String(cursor.getMonth() + 1).padStart(2, '0');
                    const d = String(cursor.getDate()).padStart(2, '0');
                    options.push(`${y}-${m}-${d}`);
                }

                // 다음 수요일로 +7일
                cursor.setDate(cursor.getDate() + 7);
            }

            // 가장 과거 -> 최근 순으로 되어 있으므로, 최근 순 정렬이 필요하면 뒤집기
            options.reverse();
        } else {
            // 주정산: 현재 달 + 이전달까지의 매주 수요일 목록 생성
            const now = new Date();
            // 이전 달의 첫째 날
            const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            // 현재 달의 마지막 날
            const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

            // prevMonthStart부터 currentMonthEnd까지 순회하며 수요일만 수집
            const cursor = new Date(prevMonthStart);

            // 첫 수요일로 이동
            const day = cursor.getDay(); // 0=일, 3=수
            const toNextWednesday = (3 - day + 7) % 7; // 수요일까지 남은 일수
            cursor.setDate(cursor.getDate() + toNextWednesday);

            while (cursor <= currentMonthEnd) {
                // 현재 날짜보다 이전인 수요일만 포함 (미래 수요일 제외)
                if (cursor < now) {
                    const y = cursor.getFullYear();
                    const m = String(cursor.getMonth() + 1).padStart(2, '0');
                    const d = String(cursor.getDate()).padStart(2, '0');
                    options.push(`${y}-${m}-${d}`);
                }

                // 다음 수요일로 +7일
                cursor.setDate(cursor.getDate() + 7);
            }

            // 가장 과거 -> 최근 순으로 되어 있으므로, 최근 순 정렬이 필요하면 뒤집기
            options.reverse();
        }

        return options;
    })();

    // toDate 선택용: preAcctYn에 따라 다른 로직
    const toDateOptions = (() => {
        if (preAcctYn === 'Y') {
            // 선정산: fromDate 기준 7일치 옵션 (fromDate부터 6일 후까지)
            if (fromDate) {
                const startDate = parseDateYmd(fromDate);
                if (!isNaN(startDate.getTime())) {
                    const options: string[] = [];
                    for (let i = 0; i < 7; i++) {
                        const date = new Date(startDate);
                        date.setDate(startDate.getDate() + i);
                        const y = date.getFullYear();
                        const m = String(date.getMonth() + 1).padStart(2, '0');
                        const d = String(date.getDate()).padStart(2, '0');
                        options.push(`${y}-${m}-${d}`);
                    }
                    return options;
                }
            }
            return [];
        } else {
            // 주정산: 각 수요일의 +6일(다음주 화요일) 목록
            return dateOptions && dateOptions.map((ymd) => {
                const base = new Date(parseInt(ymd.slice(0, 4), 10), parseInt(ymd.slice(5, 7), 10) - 1, parseInt(ymd.slice(8, 10), 10));
                const end = new Date(base);
                end.setDate(base.getDate() + 6);
                const y = end.getFullYear();
                const m = String(end.getMonth() + 1).padStart(2, '0');
                const d = String(end.getDate()).padStart(2, '0');
                return `${y}-${m}-${d}`;
            });
        }
    })();

    /**
     * pre-acct-yn 조회 및 초기 날짜 설정
     */
    const initializeDeliveryHistory = useCallback(async () => {
        try {
            const preAcctData = await getPreAcctYn();

            setPreAcctYn(preAcctData.preAcctYn);
            
            // 가장 최근 수요일 계산
            const today = new Date();
            const dayOfWeek = today.getDay();
            const daysToSubtract = dayOfWeek >= 3 ? dayOfWeek - 3 : dayOfWeek + 4;
            const lastWednesday = new Date(today);
            lastWednesday.setDate(today.getDate() - daysToSubtract);
            const y = lastWednesday.getFullYear();
            const m = String(lastWednesday.getMonth() + 1).padStart(2, '0');
            const d = String(lastWednesday.getDate()).padStart(2, '0');
            const lastWednesdayStr = `${y}-${m}-${d}`;
            
            // pre-acct-yn에 관계없이 현재 일자 기준 가장 최신 수요일을 fromDate로 설정
            setFromDate(lastWednesdayStr);
            
            setIsInitialized(true);
        } catch (err) {
            setPreAcctYn('N');
            
            const today = new Date();
            const dayOfWeek = today.getDay();
            const daysToSubtract = dayOfWeek >= 3 ? dayOfWeek - 3 : dayOfWeek + 4;
            const lastWednesday = new Date(today);
            lastWednesday.setDate(today.getDate() - daysToSubtract);
            const y = lastWednesday.getFullYear();
            const m = String(lastWednesday.getMonth() + 1).padStart(2, '0');
            const d = String(lastWednesday.getDate()).padStart(2, '0');
            setFromDate(`${y}-${m}-${d}`);
            
            setIsInitialized(true);
        }
    }, []);

    // 날짜를 YYYYMMDD 형식으로 변환
    const fromDateYyyymmdd = fromDate.replace(/-/g, '');
    const toDateYyyymmdd = toDate.replace(/-/g, '');


    // 내부 공통 포맷터: Date -> YYYY-MM-DD
    const formatDateYmd = useCallback((date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }, []);

    // 컴포넌트 마운트 시 초기화
    useEffect(() => {
        if (!isInitialized) {
            initializeDeliveryHistory();
        }
    }, [isInitialized, initializeDeliveryHistory]);

    // fromDate가 설정되었을 때 toDate 자동 설정
    useEffect(() => {
        if (fromDate && !toDate) {
            const start = parseDateYmd(fromDate);
            if (!isNaN(start.getTime())) {
                if (preAcctYn === 'Y') {
                    // 선정산: fromDate + 6일 (7일치)
                    const end = new Date(start);
                    end.setDate(start.getDate() + 6);
                    const expectedToDate = formatDateYmd(end);
                    setToDate(expectedToDate);
                } else {
                    // 주정산: fromDate + 6일
                    const end = new Date(start);
                    end.setDate(start.getDate() + 6);
                    const expectedToDate = formatDateYmd(end);
                    setToDate(expectedToDate);
                }
            }
        }
    }, [fromDate, toDate, preAcctYn, parseDateYmd, formatDateYmd]);

    // 단일 동기화 이펙트: 마지막 변경 필드 기준으로 반대편 값을 맞춤
    useEffect(() => {
        if (!fromDate && !toDate) return;

        if (lastChangedRef.current === 'from') {
            // 선정산인 경우 fromDate 변경 시 toDate를 자동으로 조정하지 않음
            if (preAcctYn === 'Y') {
                lastChangedRef.current = null;
                return;
            }
            
            // 주정산인 경우에만 fromDate 변경 시 toDate를 자동으로 조정
            const start = parseDateYmd(fromDate);
            if (isNaN(start.getTime())) return;
            const end = new Date(start);
            end.setDate(start.getDate() + 6);
            const expectedToDate = formatDateYmd(end);
            if (toDate !== expectedToDate) {
                setToDate(expectedToDate);
            }
            lastChangedRef.current = null;
            return;
        }

        if (lastChangedRef.current === 'to') {
            // 선정산인 경우 toDate 변경 시 fromDate를 자동으로 조정하지 않음
            if (preAcctYn === 'Y') {
                lastChangedRef.current = null;
                return;
            }
            
            // 주정산인 경우에만 toDate 변경 시 fromDate를 자동으로 조정
            const end = parseDateYmd(toDate);
            if (isNaN(end.getTime())) return;
            const start = new Date(end);
            start.setDate(end.getDate() - 6);
            const expectedFromDate = formatDateYmd(start);
            if (fromDate !== expectedFromDate) {
                setFromDate(expectedFromDate);
            }
            lastChangedRef.current = null;
            return;
        }
    }, [fromDate, toDate, formatDateYmd, parseDateYmd, preAcctYn]);

    /**
     * 배달 이력 데이터 조회
     */
    const fetchDeliveryHistory = useCallback(async () => {
        if (!fromDateYyyymmdd || !toDateYyyymmdd) {
            return;
        }

        // 주정산인 경우에만 동기화 보정 검증
        if (preAcctYn === 'N') {
            // 동기화 보정: fromDate 기준 toDate가 정확히 +6일인지 확인, 아니면 대기
            const start = parseDateYmd(fromDate);
            if (!isNaN(start.getTime())) {
                const end = new Date(start);
                end.setDate(start.getDate() + 6);
                const expectedTo = formatDateYmd(end).replace(/-/g, '');
                if (toDateYyyymmdd !== expectedTo) {
                    return; // 보정 중이므로 API 호출을 스킵
                }
            }

            // 동기화 보정: toDate 기준 fromDate가 정확히 -6일인지 확인, 아니면 대기
            const end = parseDateYmd(toDate);
            if (!isNaN(end.getTime())) {
                const start = new Date(end);
                start.setDate(end.getDate() - 6);
                const expectedFrom = formatDateYmd(start).replace(/-/g, '');
                if (fromDateYyyymmdd !== expectedFrom) {
                    return; // 보정 중이므로 API 호출을 스킵
                }
            }
        }

        try {
            // 토큰에 riderId, suppId가 포함되어 있으므로 파라미터에서 제외
            const requestParams: DeliveryHistoryRequest = {
                fromDate: fromDateYyyymmdd,
                toDate: toDateYyyymmdd,
            };

            const responseData = await getDeliveryHistory(requestParams);

            // API 응답에서 직접 통계 데이터 사용
            if (responseData.success) {
                const totalCompleted = responseData.totFinishCnt || 0;
                const totalRejected = responseData.totRejectCnt || 0;
                const totalRequests = totalCompleted + totalRejected + (responseData.totAssingCancelCnt || 0) + (responseData.totDeliCancelCnt || 0);
                const acceptanceRate = totalRequests > 0 ? Math.round((totalCompleted / totalRequests) * 100) : 0;
                
                setDeliveryStats({
                    completedCount: totalCompleted,
                    rejectedCount: totalRejected,
                    acceptanceRate: acceptanceRate,
                    deliveryFee: responseData.totDeliveryAmt || 0,
                    morningLunchPeakCount: responseData.totMorTimeCnt || 0,
                    afternoonNonPeakCount: responseData.totAfterNonCnt || 0,
                    eveningPeakCount: responseData.totNightNonCnt || 0,
                    nightPeakCount: responseData.totNightCnt || 0,
                    foodCount: responseData.totFinishFoodCnt || 0,
                    bmartCount: responseData.totFinishBmartCnt || 0,
                    storeCount: responseData.totFinishStoreCnt || 0,
                    hourCounts: responseData.totHourCounts?.map(Number) ?? new Array(24).fill(0),
                });
            } else {
                // 데이터가 없으면 기본값 유지
                setDeliveryStats(DEFAULT_DELIVERY_HISTORY_STATS);
            }
        } catch (err) {
            setDeliveryStats(DEFAULT_DELIVERY_HISTORY_STATS);
        }
    }, [fromDateYyyymmdd, toDateYyyymmdd, parseDateYmd, formatDateYmd, fromDate, toDate, preAcctYn]);

    /**
     * 날짜 변경 핸들러
     */
    const handleFromDateChange = useCallback((date: string) => {
        lastChangedRef.current = 'from';
        setFromDate(date);
    }, []);

    const handleToDateChange = useCallback((date: string) => {
        lastChangedRef.current = 'to';
        setToDate(date);
    }, []);

    /**
     * 데이터 새로고침
     */
    const refreshData = useCallback(() => {
        fetchDeliveryHistory();
    }, [fetchDeliveryHistory]);

    // 날짜가 변경될 때마다 데이터 조회
    useEffect(() => {
        if (fromDateYyyymmdd && toDateYyyymmdd) {
            fetchDeliveryHistory();
        }
    }, [fromDateYyyymmdd, toDateYyyymmdd, fetchDeliveryHistory]);

    return {
        // 상태
        deliveryStats,
        fromDate,
        toDate,
        dateOptions,
        toDateOptions,
        preAcctYn,
        
        // 액션
        handleFromDateChange,
        handleToDateChange,
        refreshData,
        
        // 헬퍼
        isDataLoaded: deliveryStats.completedCount > 0 || deliveryStats.rejectedCount > 0,
        isLoading: !isInitialized || !fromDate || !toDate,
    };
};
